import React, { useEffect, useMemo, useState } from 'react';
import qrcode from 'qrcode-generator';
import { CLIENT_URL } from '../../lib/host';
import { billingService } from '../../services/billingService';

// Экран для уволенного сотрудника: вместо «учётная запись деактивирована,
// обратитесь к администратору» — предложение продолжить в клиентском Ньютоне.
// Решение Владимира 16.10.2026, идея Лизы; макет согласован тогда же.
//
// Кто «уволен», решает bit-auth (departure.py) по справочнику сотрудников, и
// отвечает он только владельцу отвергнутого ключа — по адресу этот экран
// не получить. Записи корпоративной учётки в клиентскую не переезжают: это
// данные компании, и экран говорит об этом прямо.
//
// Показы считает bit-auth (событие departure_shown в /departure): журнал событий
// фронта отвергнутый ключ не примет. Дошедших до клиентского Ньютона — метка
// from=staff-exit.

const rub = (n) => Math.round(n).toLocaleString('ru-RU').replace(/\s/g, ' ');

// Строки про цены — из действующих тарифов bit-billing: зашитая цифра
// разошлась бы с витриной при первой же смене цен.
function priceLines(plans) {
  const free = plans.find((p) => !p.price_month_rub);
  const paid = plans.filter((p) => p.price_month_rub > 0).map((p) => p.price_month_rub);
  const lines = [];
  if (free?.minutes_per_period) {
    lines.push(free.overage_rub_per_minute
      ? `${free.minutes_per_period} минут в месяц без оплаты, дальше ${rub(free.overage_rub_per_minute)} ₽ за минуту`
      : `${free.minutes_per_period} минут в месяц без оплаты`);
  }
  if (paid.length) lines.push(`или тариф от ${rub(Math.min(...paid))} ₽ в месяц`);
  return lines;
}

function QrCode({ text }) {
  const qr = useMemo(() => {
    const q = qrcode(0, 'M');
    q.addData(text);
    q.make();
    return q;
  }, [text]);
  const n = qr.getModuleCount();
  const cells = [];
  for (let r = 0; r < n; r += 1) {
    for (let c = 0; c < n; c += 1) {
      if (qr.isDark(r, c)) cells.push(<rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" />);
    }
  }
  return (
    <svg data-testid="departed-qr" role="img" aria-label={`QR-код: ${text}`}
         viewBox={`-2 -2 ${n + 4} ${n + 4}`} className="w-full h-auto" shapeRendering="crispEdges">
      <rect x="-2" y="-2" width={n + 4} height={n + 4} fill="#fff" />
      <g fill="#000">{cells}</g>
    </svg>
  );
}

export default function DepartedScreen({ info, onLeave }) {
  const [prices, setPrices] = useState([]);
  const url = CLIENT_URL ? `${CLIENT_URL}?from=staff-exit` : '';

  useEffect(() => {
    let alive = true;
    billingService.getPlans()
      .then((d) => { if (alive) setPrices(priceLines(d?.plans || [])); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper-bg px-4 py-8">
      <div className="w-full max-w-[640px] bg-paper-card rounded-[20px] p-6 sm:p-8
                      grid gap-7 sm:grid-cols-[1fr_168px] items-start">
        <div className="flex flex-col">
          <h1 className="m-0 mb-2.5 font-display text-[22px] font-bold leading-[1.25] text-paper-ink">
            Доступ сотрудника закрыт
          </h1>
          <p className="m-0 mb-3.5 text-[14px] leading-[1.5] text-paper-ink-2">
            Учётная запись <b className="text-paper-ink">{info.email}</b> отключена: по данным
            Первого Бита вы больше не работаете в компании. Записи и протоколы этой учётной
            записи остаются у компании.
          </p>
          {url && (
            <>
              <div className="rounded-panel bg-paper-panel p-4 mb-[18px] text-[14px]">
                <b className="block font-semibold mb-1 text-paper-ink">
                  Ньютоном можно пользоваться дальше — как клиент
                </b>
                <ul className="m-0 mt-1.5 pl-[18px] list-disc text-paper-ink-2">
                  <li>вход по личной почте</li>
                  {prices.map((line) => <li key={line}>{line}</li>)}
                </ul>
              </div>
              <a
                href={url}
                className="block text-center bg-paper-accent hover:bg-paper-accent-hover text-white
                           font-semibold rounded-control px-4 py-[13px] no-underline"
              >
                Перейти в клиентский Ньютон
              </a>
            </>
          )}
          <p className="mt-3.5 mb-0 text-[12px] text-paper-ink-3">
            Работаете в Первом Бите и это ошибка? Напишите на{' '}
            <a href="mailto:support@1bitai.ru" className="text-paper-ink-2">support@1bitai.ru</a>
            {' '}— разберёмся и вернём доступ.
          </p>
          <button type="button" onClick={onLeave}
                  className="self-start mt-3 text-[12px] text-paper-ink-3 hover:text-paper-ink">
            Выйти
          </button>
        </div>
        {/* На телефоне QR не нужен: человек уже на телефоне, ему хватит кнопки. */}
        {url && (
          <div className="hidden sm:block">
            <div className="bg-white border border-paper-line rounded-panel p-2.5">
              <QrCode text={url} />
            </div>
            <p className="mt-2 text-center text-[12px] leading-[1.4] text-paper-ink-3">
              Наведите камеру телефона — откроется клиентский Ньютон
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
